import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  errors,
  paxRoute,
  type RouteAuth,
  sessionRequired,
  staffAuth,
  unauthorizedMessage,
} from "../../common";
import {
  appealOpen,
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import {
  CheckUsernameAvailabilityQuery,
  CheckUsernameAvailabilityResponse,
  CreateImageUploadRequest,
  CreateImageUploadResponse,
  ImageRejection,
  PermissionsResponse,
  ProfileIdParams,
  ProfileImageKindParams,
  ProfileResponse,
  ProfileStatsResponse,
  SearchAccountsQuery,
  SearchAccountsResponse,
  SearchProfilesQuery,
  SearchProfilesResponse,
  SetProfileImageRequest,
  SetUsernameRequest,
  SetUsernameResponse,
  UpdateProfileRequest,
  UsernameParams,
} from "./schemas";

export const service = {
  group: "profiles",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Profiles API",
  description:
    "Public profiles, owner-managed profile fields, claimed usernames, and your own permissions.",
} as const satisfies ContractService;

const profileReaderAuth: RouteAuth = {
  kind: "session",
  note: "Guests have no profile, so their ids return 404.",
};

const accountErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

const ownerAuth: RouteAuth = { kind: "account", note: "Only the profile's owner." };

const ownerErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: "Only the profile's owner may do this." },
} as const;

export const get = oc
  .meta(
    paxRoute({ auth: profileReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/{profileId}",
      operationId: "profiles.get",
      summary: "Get a public profile.",
      description:
        "Returns one profile by its stable profile id. Only registered accounts have profiles, so a guest's id returns 404.",
      inputStructure: "detailed",
      successDescription: "The public profile.",
    }),
  )
  .errors({
    BAD_REQUEST: { message: "profileId must be a valid UUID." },
    NOT_FOUND: { message: "No public profile has that id." },
  })
  .input(z.object({ params: ProfileIdParams }))
  .output(ProfileResponse);

export const getByUsername = oc
  .meta(
    paxRoute({ auth: profileReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/by-username/{username}",
      operationId: "profiles.byUsername.get",
      summary: "Get a public profile by username.",
      description:
        "Returns the profile that currently holds a username, ignoring case. A username nobody holds returns 404, including one a profile held before renaming.",
      inputStructure: "detailed",
      successDescription: "The public profile.",
    }),
  )
  .errors({
    BAD_REQUEST: { message: "username must be a valid username." },
    NOT_FOUND: { message: "No public profile has that username." },
  })
  .input(z.object({ params: UsernameParams }))
  .output(ProfileResponse);

export const getStats = oc
  .meta(
    paxRoute({ auth: profileReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/{profileId}/stats",
      operationId: "profiles.stats.get",
      summary: "Get a profile's play stats.",
      description:
        "Returns the rounds a profile has played and the time it has spent playing, from ClickHouse's daily rollups. It is separate from the profile so the profile never waits on ClickHouse.",
      inputStructure: "detailed",
      successDescription: "The profile's play totals.",
    }),
  )
  .errors({
    BAD_REQUEST: { message: "profileId must be a valid UUID." },
    NOT_FOUND: { message: "No public profile has that id." },
    SERVICE_UNAVAILABLE: { message: "Play stats are unavailable right now; try again shortly." },
  })
  .input(z.object({ params: ProfileIdParams }))
  .output(ProfileStatsResponse);

export const update = oc
  .meta(
    paxRoute({ auth: ownerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/profiles/v1alpha1/{profileId}",
      operationId: "profiles.update",
      summary: "Update a profile.",
      description:
        "Replaces the supplied public profile fields. Omitted fields stay unchanged and null clears a nullable field. Username changes use the dedicated username endpoint. Automod checks each changed field on its own; if any is refused, nothing is saved.",
      inputStructure: "detailed",
      successDescription: "The updated public profile.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...appealOpen,
    ...moderationRefused,
    ...automodUnavailable,
    BAD_REQUEST: {
      message: "Provide at least one profile field and use valid field values.",
    },
    ...ownerErrors,
  })
  .input(z.object({ params: ProfileIdParams, body: UpdateProfileRequest }))
  .output(ProfileResponse);

export const setUsername = oc
  .meta(
    paxRoute({ auth: ownerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/profiles/v1alpha1/{profileId}/username",
      operationId: "profiles.username.set",
      summary: "Claim or change a username.",
      description:
        "Claims the profile's first username or changes its current username. The initial claim is free; later normalized changes are limited to two in the rolling previous 90 days. Repeating the current normalized username changes only its display casing and consumes no rename. Automod checks a new username first; a refused one isn't saved and consumes no rename.",
      inputStructure: "detailed",
      successDescription: "The updated profile and remaining rename allowance.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...appealOpen,
    ...moderationRefused,
    ...automodUnavailable,
    BAD_REQUEST: {
      message: "The username does not meet the username format rules.",
    },
    ...ownerErrors,
    CONFLICT: {
      message: "The username is taken, or the profile has reached its username rename limit.",
    },
  })
  .input(z.object({ params: ProfileIdParams, body: SetUsernameRequest }))
  .output(SetUsernameResponse);

export const checkUsernameAvailability = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/username-availability",
      operationId: "profiles.usernameAvailability.get",
      summary: "Check username availability.",
      description:
        "Checks raw username input against format rules, current ownership, uniqueness, and the profile's rolling rename allowance.",
      inputStructure: "detailed",
      successDescription: "The current availability result.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message: "The query must contain one username string.",
    },
    ...accountErrors,
  })
  .input(z.object({ query: CheckUsernameAvailabilityQuery }))
  .output(CheckUsernameAvailabilityResponse);

export const search = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/search",
      operationId: "profiles.search",
      summary: "Search profiles by profile id or username prefix.",
      description:
        "Finds a connected profile by exact profile id, including one without a username, or finds profiles with claimed usernames by case-insensitive prefix. Username matches are ordered by normalized username and profile id; no total count is returned.",
      inputStructure: "detailed",
      successDescription: "Profiles matching the exact id or username prefix.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "q must be a profile id or the start of a username, 2–64 characters; limit must be 1–10.",
    },
    ...accountErrors,
  })
  .input(z.object({ query: SearchProfilesQuery }))
  .output(SearchProfilesResponse);

export const searchAccounts = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/accounts/search",
      operationId: "profiles.accounts.search",
      summary: "Find accounts.",
      description:
        "Finds accounts by exact user id, by the start of their email, or by the start of their username, with each one's email so staff can tell them apart. Accounts without a profile are included.",
      inputStructure: "detailed",
      successDescription: "The matching accounts.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ query: SearchAccountsQuery }))
  .output(SearchAccountsResponse);

export const createImageUpload = oc
  .meta(
    paxRoute({ auth: ownerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/profiles/v1alpha1/{profileId}/images/{kind}/uploads",
      operationId: "profiles.images.uploads.create",
      summary: "Start an avatar or banner upload.",
      description:
        "Returns the upload's id and a presigned URL that accepts a PUT of the image, with exactly the content type and size in the request, for 5 minutes. Images are JPEG, PNG, WebP, or GIF, up to 7 MiB.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The upload's id and where to upload the image.",
    }),
  )
  .errors({
    ...surfaceBanned,
    BAD_REQUEST: {
      message: "The content type is not an accepted image type, or the size is over 7 MiB.",
    },
    ...ownerErrors,
  })
  .input(z.object({ params: ProfileImageKindParams, body: CreateImageUploadRequest }))
  .output(CreateImageUploadResponse);

export const setImage = oc
  .meta(
    paxRoute({ auth: ownerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/profiles/v1alpha1/{profileId}/images/{kind}",
      operationId: "profiles.images.set",
      summary: "Set an avatar or banner.",
      description:
        "Processes an uploaded image and shows it as the avatar or banner. The response waits for processing and an automod check, which usually take a few seconds. A refused image isn't shown.",
      inputStructure: "detailed",
      successDescription: "The profile with its new image URL.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...appealOpen,
    ...moderationRefused,
    ...automodUnavailable,
    BAD_REQUEST: { message: "uploadId must be 12 lowercase letters and digits." },
    ...ownerErrors,
    NOT_FOUND: { message: "Nothing was uploaded under that id for this image." },
    UNPROCESSABLE_CONTENT: { message: "The image could not be processed.", data: ImageRejection },
    BAD_GATEWAY: {
      message: "Image processing is unavailable right now; start a new upload and try again.",
    },
  })
  .input(z.object({ params: ProfileImageKindParams, body: SetProfileImageRequest }))
  .output(ProfileResponse);

export const deleteImage = oc
  .meta(
    paxRoute({ auth: ownerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/profiles/v1alpha1/{profileId}/images/{kind}",
      operationId: "profiles.images.delete",
      summary: "Remove an avatar or banner.",
      inputStructure: "detailed",
      successDescription: "The profile without that image.",
    }),
  )
  .errors({
    ...appealOpen,
    ...ownerErrors,
  })
  .input(z.object({ params: ProfileImageKindParams }))
  .output(ProfileResponse);

export const getPermissions = oc
  .meta(
    paxRoute({
      auth: {
        kind: "account",
        note: "The profile's owner, or staff with `staff.manage`.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/profiles/v1alpha1/{profileId}/permissions",
      operationId: "profiles.permissions.get",
      summary: "Get a person's staff permissions.",
      description:
        "Returns the permissions the person holds, or null when they hold none, and `isAdmin: true` when they are an admin. Clients read their own to decide which staff tools to show; each staff route checks again on its own.",
      inputStructure: "detailed",
      successDescription: "The person's permissions.",
    }),
  )
  .errors({
    UNAUTHORIZED: { message: unauthorizedMessage },
    FORBIDDEN: {
      message: "Only the profile's owner, or staff with staff.manage, may read this.",
    },
  })
  .input(z.object({ params: ProfileIdParams }))
  .output(PermissionsResponse);
